import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHero } from "@/components/Sections";
import { Send } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send right now. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not send right now.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Get in"
        accent="touch."
        subtitle="For inquiries related to Bronto Cosmos, Bronto Intelligence, our platforms, partnerships, media, or career opportunities, please reach out using the form below."
      />

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="glass-strong p-6 md:p-10">
              <p className="text-sm text-white/60 mb-6">
                Messages are routed to <span className="text-white">info@brontointelligence.com</span>.
              </p>
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <Field label="Name" name="name" value={form.name} onChange={onChange} placeholder="Your name" testid="contact-name" />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@example.com" testid="contact-email" />
                <div>
                  <label className="eyebrow block mb-3">Message</label>
                  <textarea
                    name="message"
                    rows={7}
                    value={form.message}
                    onChange={onChange}
                    placeholder="How can we help?"
                    data-testid="contact-message"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-4 text-[15px] text-white placeholder:text-white/35 focus:outline-none focus:border-white/30 transition-colors resize-none"
                  />
                </div>
                <div className="pt-2">
                  <button type="submit" disabled={sending} className="btn-pill btn-primary disabled:opacity-60 disabled:cursor-not-allowed" data-testid="contact-submit">
                    {sending ? "Sending…" : (<>Send Message <Send size={14} /></>)}
                  </button>
                  {sent && (
                    <span className="ml-4 text-sm text-emerald-300" data-testid="contact-sent">Message sent successfully.</span>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-5">
            <InfoCard title="General" body="For general inquiries, partnerships, and information about Bronto Cosmos." />
            <InfoCard title="Media" body="For journalists, editors, and press representatives covering Bronto Intelligence." />
            <InfoCard title="Careers" body="For career opportunities and recruitment across the Bronto ecosystem." />
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="eyebrow block mb-3">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/[0.04] border border-white/10 rounded-full px-5 py-3 text-[15px] text-white placeholder:text-white/35 focus:outline-none focus:border-white/30 transition-colors"
      />
    </div>
  );
}

function InfoCard({ title, body }) {
  return (
    <div className="glass p-6">
      <div className="display text-xl">{title}</div>
      <p className="mt-3 text-sm text-white/60 leading-relaxed">{body}</p>
    </div>
  );
}
